import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarehouse } from '@fortawesome/free-solid-svg-icons';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(ArcElement, CategoryScale, LinearScale, Tooltip, Legend);

const InventoryStatusPercentageCard = () => {
  const [inventoryData, setInventoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchInventory = async () => {
      try {
        const response = await axios.get('https://api.spherewms.com/v2/wms/wisdom-s/inventory', {
          headers: {
            Authorization: 'Bearer edfcdfb396fca8cd94fba87c8c3592a1',
          },
        });
        setInventoryData(formatResponseData(response.data).sort((a, b) => b.total_on_hand - a.total_on_hand)); // Sort by total_on_hand descending
        setIsLoading(false);
      } catch (error) {
        console.error("Failed to fetch inventory:", error);
        setError("Failed to fetch inventory data.");
        setIsLoading(false);
      }
    };

    fetchInventory();
  }, []);

  const formatResponseData = (data) => {
    let formattedData = [];
    Object.entries(data).forEach(([clientCode, products]) => {
      Object.values(products).forEach((product) => {
        formattedData.push({ clientCode, ...product });
      });
    });
    return formattedData;
  };

  const totalItems = inventoryData.reduce((acc, item) => acc + item.total_on_hand, 0);
  const chartData = {
    labels: inventoryData.map(item => item.clientCode),
    datasets: [
      {
        label: 'Percentage of Inventory by Total On Hand',
        data: inventoryData.map(item => ((item.total_on_hand / totalItems) * 100).toFixed(2)),
        backgroundColor: ['#4B77BE', '#5CACEE', '#77B1A9', '#72C6EF', '#8E44AD', '#3498DB', '#2ECC71', '#E74C3C'],
        hoverBackgroundColor: ['#354A5F', '#3E6B8E', '#5D8A76', '#5DA0C3', '#6C3483', '#2980B9', '#27AE60', '#C0392B'],
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return `${context.label}: ${context.parsed}%`;
          },
        },
      },
    },
  };

  return (
    <div className="bg-white shadow rounded-lg p-4 w-full max-w-lg h-custom-card-big flex flex-col justify-between">
      <div className="mb-4">
        <h2 className="text-lg font-bold text-gray-800 flex items-center">
          <FontAwesomeIcon icon={faWarehouse} className="text-blue-600 mr-2" />
          Inventory Status Percentages
        </h2>
      </div>
      <div className="flex-grow">
        <Pie data={chartData} options={options} />
      </div>
      <hr className="my-4" />
      <div className="overflow-auto" style={{ maxHeight: '200px' }}>
        <table className="min-w-full">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client Code</th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total On Hand</th>
              {/* Add other headers as needed */}
            </tr>
          </thead>
          <tbody className="bg-white">
            {inventoryData.map((item, index) => (
              <tr key={index} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{item.clientCode}</td>
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">{item.total_on_hand}</td>
                {/* Add other data cells as needed */}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InventoryStatusPercentageCard;
